/**
 * Le rendez-vous : date, heure, et compte à rebours.
 *
 * ─── Ce qui est enregistré, et où ──────────────────────────────────────────
 *
 * La **date** part au serveur, dans les champs qui existent déjà :
 *   `status = 'APPOINTMENT_SET'` et `nextFollowUpAt = 'AAAA-MM-JJ'`.
 * Elle est donc partagée entre appareils, sauvegardée avec la bibliothèque, et
 * exportée comme le reste. C'est elle qui fait foi.
 *
 * L'**heure** reste sur l'appareil (`localStorage`). La colonne SQL est un
 * `DATE` et la validation du serveur rejette tout ce qui n'est pas
 * `AAAA-MM-JJ` (`server/src/validation/prospectInput.ts`). Ajouter l'heure
 * demanderait une migration de schéma — donc une modification du backend, que
 * cette interface parallèle s'interdit : elle doit pouvoir être abandonnée en
 * supprimant un dossier, sans laisser de trace côté serveur.
 *
 * La conséquence est assumée et visible à l'écran : sans heure enregistrée, la
 * fiche affiche la date seule. L'heure est un pense-bête local, pas une donnée
 * du produit. Le jour où le backend gagnera un `TIMESTAMP`, il n'y aura qu'à
 * remplacer les trois fonctions de stockage ci-dessous.
 */

/* ==========================================================================
   Dates
   ========================================================================== */

const pad = (n: number): string => String(n).padStart(2, '0');

/** `AAAA-MM-JJ` dans le fuseau local — jamais `toISOString()`, qui décale. */
export function toIsoDay(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function todayIso(now: Date = new Date()): string {
  return toIsoDay(now);
}

export function isoPlusDays(days: number, now: Date = new Date()): string {
  const date = new Date(now);
  date.setDate(date.getDate() + days);
  return toIsoDay(date);
}

/**
 * Le serveur peut rendre `2026-08-25` comme `2026-08-25T00:00:00.000Z` selon
 * le pilote SQL. On ne garde que le jour : c'est tout ce qui est stocké.
 */
export function dayOf(value: string | null): string | null {
  if (value === null) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return trimmed.slice(0, 10);
}

/**
 * Interprété à **midi**, volontairement.
 *
 * `new Date('2026-08-25')` est lu comme minuit UTC : à l'ouest de Greenwich la
 * date affichée recule d'un jour. Midi local met les deux bornes hors de
 * portée de n'importe quel décalage horaire ou changement d'heure.
 */
function atNoon(isoDay: string): Date {
  return new Date(`${isoDay}T12:00:00`);
}

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/** « Mardi 25 août ». */
export function dayLong(isoDay: string): string {
  return capitalize(
    atNoon(isoDay).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }),
  );
}

/** « Mar. 25 août ». */
export function dayShort(isoDay: string): string {
  return capitalize(
    atNoon(isoDay).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }),
  );
}

/** Jours pleins d'ici là. Négatif = passé. `null` si la date est illisible. */
export function daysUntil(isoDay: string | null, now: Date): number | null {
  const day = dayOf(isoDay);
  if (day === null) return null;
  const target = atNoon(day);
  if (Number.isNaN(target.getTime())) return null;
  const midnight = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.round((midnight(target) - midnight(now)) / 86_400_000);
}

export function countdownLabel(days: number | null): string {
  if (days === null) return 'Sans date';
  if (days < -1) return `Il y a ${Math.abs(days)} jours`;
  if (days === -1) return 'Hier';
  if (days === 0) return "Aujourd'hui";
  if (days === 1) return 'Demain';
  return `Dans ${days} jours`;
}

/** Libellé de relance, du point de vue « ce qui est en retard ». */
export function followUpLabel(days: number | null): string {
  if (days === null) return 'Aucune relance';
  if (days < -1) return `Retard de ${Math.abs(days)} j`;
  if (days === -1) return 'Retard d’un jour';
  if (days === 0) return "Aujourd'hui";
  if (days === 1) return 'Demain';
  return `Dans ${days} j`;
}

export interface QuickDate {
  iso: string;
  label: string;
}

/**
 * Les quatre raccourcis proposés au-dessus du calendrier.
 *
 * Ils couvrent ce qui se dit réellement au téléphone : demain, après-demain,
 * dans trois jours, la semaine prochaine. Au-delà, on ouvre le calendrier —
 * qui reste juste en dessous, jamais derrière un second geste.
 */
export function quickDates(now: Date = new Date()): QuickDate[] {
  return [
    { iso: isoPlusDays(1, now), label: 'Demain' },
    { iso: isoPlusDays(2, now), label: 'Après-demain' },
    { iso: isoPlusDays(3, now), label: 'Dans 3 jours' },
    { iso: isoPlusDays(7, now), label: 'La semaine prochaine' },
  ];
}

/* ==========================================================================
   L'heure, côté appareil
   ========================================================================== */

const TIME_KEY = 'hermes.rdvTime';

type TimeMap = Record<string, string>;

function readMap(): TimeMap {
  try {
    const raw = window.localStorage.getItem(TIME_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as TimeMap;
  } catch {
    // Stockage indisponible ou contenu illisible : pas d'heure, c'est tout.
    // Une heure perdue ne doit jamais empêcher la fiche de s'afficher.
    return {};
  }
}

function writeMap(map: TimeMap): void {
  try {
    window.localStorage.setItem(TIME_KEY, JSON.stringify(map));
  } catch {
    // Mode privé, quota plein : l'heure ne vaudra que pour cette visite.
  }
}

/** `HH:MM`, ou `null` si aucune heure n'a été notée sur cet appareil. */
export function readTime(prospectId: number): string | null {
  return readMap()[String(prospectId)] ?? null;
}

export function writeTime(prospectId: number, time: string): void {
  const map = readMap();
  map[String(prospectId)] = time;
  writeMap(map);
}

export function clearTime(prospectId: number): void {
  const map = readMap();
  if (!(String(prospectId) in map)) return;
  delete map[String(prospectId)];
  writeMap(map);
}
